import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useIntl, FormattedMessage } from "react-intl";
import { ArrowUturnRightIcon, TrashIcon } from "@heroicons/react/24/outline";
import { NotificationsContext } from "@ui-components/Notifications";
import Button from "@ui-components/Button";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";

const FILE_EXTENSION_REGEX = /(?:\.([^.]+))?$/;

const EditorContainerComponentDefault = ({ isEditingImage, children }) => {
  return <>{isEditingImage && <div>{children}</div>}</>;
};

const CanvasEditor = ({ currentImage, onCancel, onEditComplete }) => {
  const [currentRotation, setCurrentRotation] = useState(1);
  const canvasRef = useRef(null);

  const dataURLtoBlob = (dataURL) => {
    const BASE64_MARKER = ";base64,";
    if (dataURL.indexOf(BASE64_MARKER) === -1) {
      const parts = dataURL.split(",");
      const contentType = parts[0].split(":")[1];
      const raw = decodeURIComponent(parts[1]);
      return new Blob([raw], { type: contentType });
    }
    const parts = dataURL.split(BASE64_MARKER);
    const contentType = parts[0].split(":")[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: contentType });
  };

  const handleRotateCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if ((currentRotation + 1) % 2 === 0) {
      canvas.width = currentImage.height;
      canvas.height = currentImage.width;
    } else {
      canvas.width = currentImage.width;
      canvas.height = currentImage.height;
    }
    // move to center of the canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((Math.PI / 2) * currentRotation);
    // set the transform origin at center of the image
    ctx.drawImage(
      currentImage,
      -currentImage.width / 2,
      -currentImage.height / 2
    );
    setCurrentRotation((currentRotation) => currentRotation + 1);
  };

  useEffect(() => {
    // Populate canvas with image previously uploaded and added to currentImage from the
    // input file change event handler
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = currentImage.width;
    canvas.height = currentImage.height;
    ctx.drawImage(currentImage, 0, 0, currentImage.width, currentImage.height);
  }, [currentImage]);

  return (
    <>
      <div className="flex flex-wrap md:flex-nowrap justify-center gap-4 my-5">
        <Button onClick={handleRotateCanvas} styleType="empty">
          <ArrowUturnRightIcon className="w-4 -ml-0.5" />
          <FormattedMessage id="rotate" />
        </Button>
        <Button
          onClick={() => {
            onEditComplete(
              dataURLtoBlob(canvasRef.current.toDataURL("image/jpeg"))
            );
          }}
          styleType="darkGreen"
        >
          <FormattedMessage id="confirm" />
        </Button>
        <Button onClick={onCancel} styleType="danger" className="md:ml-10">
          <TrashIcon className="w-4 mx-auto" />
          <FormattedMessage id="cancel" />
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        style={{
          maxWidth: "100%",
          maxHeight: "600px",
          objectFit: "contain",
          margin: "auto",
        }}
      />
    </>
  );
};

const ImageUploader = ({
  prefix,
  bucket,
  uploading,
  setUploading,
  onUploadComplete,
  children,
  maxSizeImageSizeMb = 5,
  setIsEditingImage = () => {},
  EditorContainerComponent = EditorContainerComponentDefault,
  accept = "image/*",
  allowEdit = false,
}) => {
  // contexts
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  // refs
  const imageInputRef = React.createRef();

  const [currentImage, setCurrentImage] = useState(null);

  // utilities
  const convertPdfToPngViaApi = async (pdfFile) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", pdfFile);

      const response = await apiRequest.post("/pdf_converter/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      });
      if (!response) {
        push({
          type: "error",
          title: intl.formatMessage({ id: "error_converting_pdf" }),
        });
        return null;
      }
      const imageBlob = response;
      const imageFile = new File([imageBlob], "converted_image.jpg", {
        type: "image/jpeg",
      });
      return imageFile;
    } catch (error) {
      console.error("Error uploading PDF or fetching image:", error);
      push({
        type: "error",
        title: intl.formatMessage({ id: "error_uploading" }),
      });
      return null;
    }
  };

  const checkUploadedImage = (file) => {
    let error = null;
    const format = FILE_EXTENSION_REGEX.exec(file.name)[1];

    if (!format) error = "Unknown file format";
    if (accept === "application/pdf" && format !== "pdf")
      error = "File must be a PDF";

    if (maxSizeImageSizeMb) {
      const sizeMb = Math.round(file.size / Math.pow(1024, 2));
      if (maxSizeImageSizeMb < sizeMb)
        error = `File size too big (max. ${maxSizeImageSizeMb}MB)`;
    }

    return { format, error };
  };

  const uploadToS3 = async (
    file,
    format,
    onUploadStart,
    onUploadProgress,
    onSafeEnd
  ) => {
    try {
      // get signed url to be used to upload image to S3
      const { url, fields } = await apiRequest.post(`/aws-signed-url`, {
        content_type: file.type,
        format: format,
        prefix: prefix,
        bucket: bucket,
        method: "POST",
      });

      // add fields to form data
      const formData = new FormData();
      Object.keys(fields).forEach((key) => {
        formData.append(key, fields[key]);
      });
      formData.append("file", file);

      // perform upload on S3
      onUploadStart();
      // --- upload image to s3
      const options = {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          "Cache-Control": "max-age=31536000",
        },
        onUploadProgress: onUploadProgress,
      };
      await axios.post(url, formData, options);
      return url + fields.key;
    } catch (error) {
      return null;
    } finally {
      onSafeEnd();
    }
  };

  const uploadImage = async (file, format) => {
    const url = await uploadToS3(
      file,
      format,
      () => {
        setUploading(true);
      },
      (progressEvent) => {
        // const percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
        // setUploadingImage(percentCompleted);
      },
      () => {
        setUploading(false);
      }
    );
    if (!url) {
      push({
        title: intl.formatMessage({ id: "server_error" }),
        type: "error",
      });
      return;
    }

    onUploadComplete(url);
  };

  const handleCancelEdit = () => {
    setCurrentImage(null);
    setUploading(false);
    setIsEditingImage(false);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleUploadFromCanvas = async (dataBlob) => {
    setUploading(true);
    setCurrentImage(null);
    uploadImage(dataBlob, "jpg");
    setIsEditingImage(false);
  };

  const handleFileInputChange = async (e) => {
    e.preventDefault();
    if (!e.target.files || e.target.files.length === 0) return;
    let file = e.target.files[0];
    let { format, error } = checkUploadedImage(file);
    if (!!error) {
      push({ title: error, type: "error" });
      return;
    }

    if (format === "pdf") {
      const imageFile = await convertPdfToPngViaApi(file);
      if (!imageFile) {
        return null;
      }
      file = imageFile;
      format = "jpg";
    }

    if (!allowEdit) {
      await uploadImage(file, format);
    } else {
      // Start editor with an image taken from the given file
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setCurrentImage(() => img);
          setIsEditingImage(() => true);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  useEffect(() => {
    setCurrentImage(() => null);
  }, []);

  return (
    <>
      <div
        className={
          uploading
            ? "pointer-events-none cursor-not-allowed"
            : "cursor-pointer"
        }
        onClick={() => {
          imageInputRef.current.click();
        }}
      >
        {children}
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        hidden
      />
      <EditorContainerComponent isEditingImage={allowEdit && currentImage}>
        <>
          <CanvasEditor
            currentImage={currentImage}
            onCancel={handleCancelEdit}
            onEditComplete={handleUploadFromCanvas}
          />
        </>
      </EditorContainerComponent>
    </>
  );
};

export default ImageUploader;
